const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/Forms/components/MetaKat/MetaKatItemBasicSelect.tsx';
import * as React from 'react';
import { ReactElement, useContext, useEffect, useMemo } from 'react';
import { cx } from '@emotion/css';
import * as MUI from '@mui/material';
import { FilterFunction, SortFunction } from '../../../../utils/data';
import { TextInput, useOptionalText, useText } from '../../../../utils/intlUtils';
import { MetaKatItem } from '../../../../defs/kisim';
import commonTextMap from '../../../../translations/translations_common';
import { FormField } from '../../defs';
import { useFormFieldBehavior } from '../hooks';
import { FormContext } from '../context';
import { getMenuItemLabel, getMenuItemValue } from './utils';
import _ from 'lodash';

export interface MetaKatItemBasicSelectProps<ValueType = MetaKatItem | undefined> extends FormField<ValueType> {
    applyDefaultValueWhenRequired?: boolean;
    favorites?: string[];
    filter?: FilterFunction<MetaKatItem>;
    labelPath?: string;
    noValueLabel?: TextInput;
    options: MetaKatItem[];
    sort?: SortFunction<MetaKatItem>;
    valuePath?: string;
}

const DEFAULT_LABEL_PATH = 'NAME';

const sxMenuItem = {
    whiteSpace: 'normal',
};

export const MetaKatItemBasicSelect = (props: MetaKatItemBasicSelectProps) => {
    const formContext = useContext(FormContext);

    const {
        applyDefaultValueWhenRequired = false,
        disabled = false,
        favorites = [],
        filter = (item: MetaKatItem) => true,
        labelPath = DEFAULT_LABEL_PATH,
        options,
        readOnly = false,
        required = false,
        sort,
        valuePath = 'FKEY',
        variant = formContext?.style.variant,
    } = props;

    const noValueLabel = useText(props.noValueLabel ?? commonTextMap.none);

    const label = useOptionalText(props.label);
    const helperText = useOptionalText(props.helperText);

    const [favoriteItems, items] = useMemo(() => {
        const filteredItems = options.filter(filter);
        const favoriteItems = filteredItems.filter(item => favorites.includes(getMenuItemValue(item, valuePath)));

        const favMap = new Map(favoriteItems.map(c => [c.FKEY, c] as const));

        const favoriteItemsSorted: MetaKatItem[] = favorites
            .map(key => favMap.get(key))
            .filter(item => !_.isUndefined(item));

        const items = filteredItems.filter(item => !favorites.includes(getMenuItemValue(item, valuePath)));

        return [favoriteItemsSorted, sort ? items.sort(sort) : items];
    }, [options, filter, sort]);

    const defaultValue = required && applyDefaultValueWhenRequired ? favoriteItems[0] || items[0] : undefined;

    const [value, setValue, errorMessage] = useFormFieldBehavior(props, defaultValue);

    useEffect(() => {
        props.onChange?.(value);
    }, [value]);

    const menuItems = useMemo(() => {
        const mapOption = (item: MetaKatItem) => {
            let label = getMenuItemLabel(item, labelPath) || getMenuItemLabel(item, DEFAULT_LABEL_PATH);
            return (
                <MUI.MenuItem key={`${item.KAT}_${item.FKEY}`} value={getMenuItemValue(item, valuePath)} sx={sxMenuItem}>
                    {label}
                </MUI.MenuItem>
            );
        };
        const mapSubTitle = (item: MetaKatItem) => <MUI.ListSubheader key={item.NAME}>{item.NAME}</MUI.ListSubheader>;

        let menuItems: ReactElement<any>[] = [];
        if (!required) {
            menuItems.push(
                <MUI.MenuItem key={`___NO_VALUE___`} value="" sx={sxMenuItem}>
                    <em>{noValueLabel}</em>
                </MUI.MenuItem>
            );
        }

        const mapItem = (item: MetaKatItem) => (item.KAT === 'ST' ? mapSubTitle(item) : mapOption(item));

        menuItems = menuItems.concat(favoriteItems.map(mapItem));
        if (favoriteItems.length) menuItems.push(<MUI.Divider key="favoritesDivider" />);
        menuItems = menuItems.concat(items.map(mapItem));
        return menuItems;
    }, [favoriteItems, items]);

    const handleTextFieldChange = (event: any) => {
        const selectedItem = options.find(item => getMenuItemValue(item, valuePath) === event.target.value);
        setValue(selectedItem);
    };

    return (
        <MUI.TextField
            className={cx('FormField', 'MetaKatItemBasicSelect', props.className, {
                disabled,
                error: !!errorMessage,
                required,
            })}
            color={errorMessage ? 'error' : 'primary'}
            disabled={disabled}
            error={!!errorMessage}
            fullWidth
            helperText={errorMessage || helperText}
            label={label}
            onChange={handleTextFieldChange}
            required={required}
            select
            size="small"
            slotProps={{
                htmlInput: {
                    readOnly,
                },
            }}
            value={value ? getMenuItemValue(value, valuePath) : ''}
            variant={variant}
        >
            {menuItems}
        </MUI.TextField>
    );
};
